// Auth state machine, mirroring global_100seconds's contexts/AuthContext.jsx:
// in-memory access token with silent-refresh-on-boot (the refresh cookie) and
// a proactive scheduled refresh before expiry.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { type Admin, Auth } from "../config/Auth";
import * as Api from "../services/Api";

const REFRESH_SKEW_SECONDS = 60;

interface AuthValue {
  admin: Admin | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(Auth.getAdmin());
  const [loading, setLoading] = useState(true);
  const timer = useRef<number | null>(null);
  const performRefreshRef = useRef<() => Promise<boolean>>(async () => false);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresIn: number) => {
      clearTimer();
      const delay = Math.max(5, expiresIn - REFRESH_SKEW_SECONDS) * 1000;
      timer.current = window.setTimeout(() => {
        void performRefreshRef.current();
      }, delay);
    },
    [clearTimer],
  );

  const performRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const res = await Api.adminRefresh();
      Auth.setSession(res.data.access_token, res.data.expires_in, res.data.admin);
      setAdmin(Auth.getAdmin());
      scheduleRefresh(res.data.expires_in);
      return true;
    } catch {
      return false;
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    performRefreshRef.current = performRefresh;
  }, [performRefresh]);

  // Silent refresh on boot (cookie-driven), then hydrate the profile.
  useEffect(() => {
    let active = true;
    (async () => {
      const ok = await performRefresh();
      if (ok && active) {
        try {
          const me = await Api.getAdminMe();
          Auth.updateAdmin(me.data);
          setAdmin(Auth.getAdmin());
        } catch {
          /* stale profile fields are acceptable */
        }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep React in sync with reactive refreshes/expiries from Request.ts.
  useEffect(() => {
    const onRefreshed = () => setAdmin(Auth.getAdmin());
    const onExpired = () => {
      setAdmin(null);
      clearTimer();
    };
    window.addEventListener("auth:token-refreshed", onRefreshed);
    window.addEventListener("auth:session-expired", onExpired);
    return () => {
      window.removeEventListener("auth:token-refreshed", onRefreshed);
      window.removeEventListener("auth:session-expired", onExpired);
    };
  }, [clearTimer]);

  const login = useCallback(
    async (username: string, password: string) => {
      setLoading(true);
      try {
        const res = await Api.adminLogin(username, password);
        Auth.setSession(res.data.access_token, res.data.expires_in, res.data.admin);
        setAdmin(Auth.getAdmin());
        scheduleRefresh(res.data.expires_in);
        return { success: true };
      } catch (err) {
        const detail =
          (err as { response?: { data?: { detail?: string; message?: string } } })?.response?.data?.detail ??
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        return { success: false, message: detail ?? "Login failed." };
      } finally {
        setLoading(false);
      }
    },
    [scheduleRefresh],
  );

  const logout = useCallback(async () => {
    clearTimer();
    Auth.clearSession();
    setAdmin(null);
    try {
      await Api.adminLogout();
    } catch {
      /* already cleared locally */
    }
  }, [clearTimer]);

  return (
    <AuthContext.Provider value={{ admin, isLoggedIn: !!admin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
